import { el, btn, backButton } from '@/ui/dom';
import { animateCount } from '@/ui/SkillPointsToast';
import { skillTier, nextSkillTier, weeklyPoints } from '@/core/skillPoints';
import { nextUnlockAt } from '@/core/Progression';
import {
  LocalLeaderboardProvider,
  type LeaderboardKind,
  type LeaderboardProvider,
} from '@/core/leaderboard';
import { vehicleLabel } from '@/ui/vehicleCatalog';
import type { SaveData } from '@/core/SaveData';

/**
 * LeaderboardScreen.ts — WORLD RANKINGS (M78): where the player's Skill Points
 * live as a career. M65 black-and-gold editorial language: rule-flanked gold
 * kicker, chrome display title, glass panels. Two blocks — YOUR CARD (rank
 * medallion, count-up Skill-Point total, tier badge, next-unlock progress bar)
 * beside the scrolling standings table; they stack in portrait and sit
 * side-by-side on a short landscape phone / desktop.
 *
 * Data comes through the async LeaderboardProvider seam (core/leaderboard.ts)
 * so the future Supabase board drops in without touching this screen.
 */
export class LeaderboardScreen {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly provider: LeaderboardProvider;
  private onBack: () => void = () => {};
  private cancelCount: () => void = () => {};
  /** Guards a stale async fetch from painting over a newer show()/hide(). */
  private showSeq = 0;
  /** M82: which board is up — the career ladder or the weekly sprint. */
  private kind: LeaderboardKind = 'alltime';
  private save: SaveData | null = null;
  /** M100: signed-out → the local-ladder banner + CREATE RACER ID push. */
  private cta: { onCreate: () => void } | null = null;

  /** M100: Flow injects the account-aware provider (falls back to the local
   *  ladder internally); no provider = the pure local board, as ever. */
  constructor(parent: HTMLElement, provider?: LeaderboardProvider) {
    this.root = el('div', 'screen screen-leaderboard screen-cine', parent);
    this.root.style.display = 'none';
    // M113: the racing-HQ hall photo (public/leaderboard/bg.webp) full-bleed
    // under the panel, plus static ambience layers built ONCE — a readability
    // scrim, a breathing glow over the wall's gold world map, a slow light
    // sweep on the glossy floor, a trophy-shelf glint and drifting dust motes.
    // All motion is CSS-only and parked by the .reduced root class (in-game
    // setting) and prefers-reduced-motion; everything is pointer-events: none.
    el('div', 'lb-bg', this.root);
    el('div', 'lb-scrim', this.root);
    const fx = el('div', 'lb-fx', this.root);
    el('div', 'lb-fx-map', fx);
    el('div', 'lb-fx-floor', fx);
    el('div', 'lb-fx-trophy', fx);
    for (let i = 1; i <= 6; i++) el('span', `lb-mote lb-mote-${i}`, fx);
    this.panel = el('div', 'lb-panel', this.root);
    // M114b — the ONE shared corner chevron replaces the footer BACK button.
    // It lives on the ROOT because render() replaces the panel's children.
    backButton(this.root, () => this.onBack());
    this.provider = provider ?? new LocalLeaderboardProvider();
  }

  show(save: SaveData, onBack: () => void, cta: { onCreate: () => void } | null = null): void {
    this.onBack = onBack;
    this.save = save;
    this.cta = cta;
    this.kind = 'alltime';
    this.refresh();
  }

  /** Fetch + paint the current board (also the tab-switch path). */
  private refresh(): void {
    const save = this.save;
    if (!save) return;
    const seq = ++this.showSeq;
    const points = this.kind === 'week' ? weeklyPoints(save) : save.lifetimePoints;
    void this.provider.fetch(points, this.kind).then((view) => {
      if (seq !== this.showSeq) return; // superseded (hidden or re-shown)
      this.render(points, view.rows, view.playerRank, save.settings.reducedMotion);
      this.root.style.display = '';
    });
  }

  private render(
    points: number,
    rows: { name: string; points: number; isPlayer: boolean; rank?: number }[],
    playerRank: number,
    reduced: boolean,
  ): void {
    this.cancelCount();
    this.panel.replaceChildren();
    // M113: the in-game reduced-motion setting parks the CSS ambience layers
    // (the HowToPlay/Onboarding root-class convention); the JS count-up below
    // keeps honoring the same flag directly.
    this.root.classList.toggle('reduced', reduced);
    const week = this.kind === 'week';

    // ----- header: kicker between gold rules + chrome title ------------------
    const head = el('div', 'lb-head', this.panel);
    const kicker = el('div', 'lb-kicker', head);
    el('span', 'mhead-rule', kicker);
    el('span', 'lb-kicker-text', kicker).textContent = 'WORLD RANKINGS';
    el('span', 'mhead-rule', kicker);
    const title = el('div', 'lb-title', head);
    title.textContent = 'LEADERBOARD';
    title.dataset.text = 'LEADERBOARD';
    el('div', 'lb-sub', head).textContent = week
      ? 'THIS WEEK · RESETS MONDAY 12 AM ET'
      : 'XP · EVERY RACE COUNTS';

    // ----- board tabs (M82): ALL TIME | THIS WEEK ----------------------------
    const tabs = el('div', 'lb-tabs', head);
    (
      [
        ['ALL TIME', 'alltime'],
        ['THIS WEEK', 'week'],
      ] as [string, LeaderboardKind][]
    ).forEach(([label, kind]) => {
      const tab = btn(label, `chip lb-tab${this.kind === kind ? ' on' : ''}`, tabs, () => {
        if (this.kind === kind) return;
        this.kind = kind;
        this.refresh();
      });
      tab.setAttribute('aria-pressed', String(this.kind === kind));
    });

    // M113: the header above floats straight over the hall photo (the wall
    // map glows behind the chrome title); everything below sits in ONE
    // floating dark-glass column with the fine gold border.
    const glass = el('div', 'lb-glass', this.panel);
    const cols = el('div', 'lb-cols', glass);

    // ----- your card ---------------------------------------------------------
    const me = el('div', 'lb-me', cols);
    const rankWrap = el('div', 'lb-me-rankwrap', me);
    const medal = el(
      'div',
      `lb-me-medal${playerRank <= 3 ? ` lb-medal-${playerRank}` : ''}`,
      rankWrap,
    );
    el('span', 'lb-me-rank', medal).textContent = `#${playerRank}`;
    el('span', 'lb-me-ranklabel', rankWrap).textContent = 'WORLD RANK';

    const totals = el('div', 'lb-me-totals', me);
    const num = el('div', 'lb-me-points', totals);
    this.cancelCount = animateCount(num, 0, points, 900, reduced);
    // M131: rankings run on XP (spending SP never drops your rank) — this is
    // an XP surface, so the green SP balance is not shown here at all.
    el('div', 'lb-me-pointslabel', totals).textContent = week ? 'XP THIS WEEK' : 'XP';

    // The tier badge stays the CAREER identity on both boards.
    const careerPoints = this.save?.lifetimePoints ?? points;
    const tier = skillTier(careerPoints);
    const tierRow = el('div', 'lb-me-tierrow', me);
    const badge = el('span', 'lb-tier', tierRow);
    badge.textContent = tier.name;
    badge.style.setProperty('--tier-accent', tier.color);
    const up = nextSkillTier(careerPoints);
    el('span', 'lb-me-tiernext', tierRow).textContent = up ? `${up.name} AT ${up.min}` : 'TOP TIER';

    // Career board: the next garage unlock. Weekly board: the reset window.
    const bar = el('div', 'lb-me-bar', me);
    const track = el('div', 'sp-bar-track', bar);
    const fill = el('div', 'sp-bar-fill', track);
    const barLabel = el('div', 'lb-me-barlabel', bar);
    // M114: pass the granted set so a legacy-granted ride is never advertised.
    const unlock = week ? null : nextUnlockAt(points, this.save?.unlockedVehicles ?? []);
    if (week) {
      fill.style.width = '100%';
      barLabel.textContent = 'FRESH BOARD EVERY MONDAY · 12 AM ET';
    } else if (unlock) {
      const threshold = points + unlock.pointsToGo;
      fill.style.width = `${Math.min(100, (points / threshold) * 100).toFixed(1)}%`;
      barLabel.textContent = `NEXT RIDE: ${vehicleLabel(unlock.id).toUpperCase()} · ${unlock.pointsToGo} XP TO GO`;
    } else {
      fill.style.width = '100%';
      barLabel.textContent = 'ALL RIDES UNLOCKED';
    }

    // ----- standings ---------------------------------------------------------
    const side = el('div', 'lb-side', cols);
    el('div', 'lb-standhead', side).textContent = 'STANDINGS';
    const list = el('div', 'lb-list', side);
    let playerRow: HTMLElement | null = null;
    rows.forEach((r, i) => {
      // M100: server rows carry their TRUE rank (the "You" row can sit far
      // below the visible top-20); local-ladder rows fall back to the index.
      const rank = r.rank ?? i + 1;
      // M113: podium rows carry a rank class so CSS can edge-accent the top 3
      // without :has() (which the iOS 15 WKWebView floor lacks).
      const podium = rank <= 3 ? ` lb-row-p${rank}` : '';
      const row = el('div', `lb-row${r.isPlayer ? ' me' : ''}${podium}`, list);
      row.style.setProperty('--row-i', String(Math.min(i, 14)));
      const rk = el('span', `lb-rank${rank <= 3 ? ` lb-medal-${rank}` : ''}`, row);
      rk.textContent = String(rank);
      el('span', 'lb-name', row).textContent = r.isPlayer ? 'You' : r.name;
      // Tier chips are a CAREER read — on the weekly sprint every row would
      // say ROOKIE, so the week's board goes without them.
      if (!week) {
        const t = skillTier(r.points);
        const chip = el('span', 'lb-tier lb-tier-mini', row);
        chip.textContent = t.name;
        chip.style.setProperty('--tier-accent', t.color);
      }
      el('span', 'lb-points', row).textContent = `${r.points} XP`;
      if (r.isPlayer) playerRow = row;
    });

    // M100: guests are ranking against the practice ladder — say so, and
    // offer the real board (one tap to the RACER ID screen).
    if (this.cta) {
      const strip = el('div', 'lb-cta', glass);
      el('span', 'lb-cta-text', strip).textContent =
        'PRACTICE LADDER · CREATE YOUR RACER ID TO ENTER THE WORLD BOARD';
      const cta = this.cta;
      btn('CREATE RACER ID', 'btn btn-gold lb-cta-btn', strip, () => cta.onCreate());
    }

    // M114b: BACK is the shared corner chevron now (built in the constructor).

    // Centre your row in the LIST only (scrollIntoView would drag the whole
    // panel/page along on small screens). .lb-list is position:relative, so
    // row offsetTop is list-local.
    requestAnimationFrame(() => {
      if (!playerRow) return;
      const row = playerRow as HTMLElement;
      list.scrollTop = Math.max(0, row.offsetTop - (list.clientHeight - row.offsetHeight) / 2);
    });
  }

  hide(): void {
    this.showSeq++;
    this.cancelCount();
    this.cancelCount = () => {};
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.cancelCount();
    this.root.remove();
  }
}
